import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { RouteConfig } from 'view/routes'
import { leftNavigationIconSet, useRouteConfigSelectors } from 'view/shared/leftNavigation'
import { Locale } from 'locale'
import { SlidingPanel } from 'view/components/panels'
import { LeftNavigationTile } from 'view/shared/leftNavigation/LeftNavigationTile'
import { LeftNavigationTileLink } from 'view/shared/leftNavigation/LeftNavigationTileLink'
import { createSelectedCounterpartyIdSelector, navigationCollapsedGetterSelector } from 'selectors/selectionSelectors'
import { SelectionActions } from 'store/selection'
import { GridId } from 'store/grid'
import { LeftNavigationCollectionStatuses } from 'view/shared/leftNavigation/LeftNavigationCollectionStatuses'
import * as constants from 'view/styles/constants'
import { DateLike } from 'utils'
import { LeftNavigationCountdownTimer } from 'view/shared/leftNavigation/LeftNavigationCountdownTimer'
import { curveLeftNavigationItemsSelector } from './CurveNegotiationSelectors'

export interface CurveLeftNavigationItem {
  counterPartyId: string
  label: string
  subTotals: [number, number, number]
  minRfqEndTime?: DateLike
  minOtwEndTime?: DateLike
}

interface CurveNegotiationsLeftNavigationProps {
  gridId?: GridId
  routeConfig: RouteConfig
}

const collectionColors: [string, string, string] = [
  constants.STATUS_COLOR_DEFAULT,
  constants.STATUS_COLOR_ACTIVE,
  constants.STATUS_COLOR_RESPOND
]

const selectedCounterpartyIdSelector = createSelectedCounterpartyIdSelector(GridId.CurvesGrid)

export const CurveNegotiationsLeftNavigation: React.FC<CurveNegotiationsLeftNavigationProps> = ({
  gridId = GridId.CurvesGrid,
  routeConfig
}) => {
  const { numberOfItems, showActivityHighlight, showAlertHighlight, showWarnHighlight, visible } = useRouteConfigSelectors(routeConfig)
  const location = useLocation()
  const dispatch = useDispatch()
  const getNavigationCollapsed = useSelector(navigationCollapsedGetterSelector)
  const selectedCounterpartyId = useSelector(selectedCounterpartyIdSelector)
  const items = useSelector(curveLeftNavigationItemsSelector)

  const hasChildren = items.length > 0
  const isNavCollapsed = getNavigationCollapsed(routeConfig.url)

  const toggleCollapsed = useCallback(
    (navigationEntry: string) => {
      const collapsed = getNavigationCollapsed(navigationEntry)
      dispatch(SelectionActions.setNavigationCollapsed(navigationEntry, !collapsed))
    },
    [getNavigationCollapsed, dispatch]
  )

  const isActive = useCallback(
    (counterpartyId: string | null) => {
      return location.pathname === routeConfig.url && selectedCounterpartyId === counterpartyId
    },
    [selectedCounterpartyId, location, routeConfig]
  )

  if (!visible) {
    return null
  }

  return (
    <>
      <LeftNavigationTile nestingLevel={0}>
        <LeftNavigationTileLink
          label={Locale.translate(routeConfig.caption)}
          numberOfItems={numberOfItems}
          showActivityHighlight={showActivityHighlight}
          showAlertHighlight={showAlertHighlight}
          showWarnHighlight={showWarnHighlight}
          url={routeConfig.url}
          icon={routeConfig.icon}
          isLeftNavigationMinimised={false}
          isSecondary={false}
          isActive={() => isActive(null)}
          onClick={() => {
            dispatch(SelectionActions.setCounterpartyId(gridId, null))
          }}
          onToggleCollapsed={hasChildren ? () => toggleCollapsed(routeConfig.url) : undefined}
          isCollapsed={isNavCollapsed}
          dataTestId={`Left-Navigation-${Locale.translate(routeConfig.caption)}-Link`}
        />
      </LeftNavigationTile>
      {hasChildren && (
        <SlidingPanel closed={isNavCollapsed} transitionOnAppear={false}>
          {items.map(item => (
            <LeftNavigationTile key={item.counterPartyId} nestingLevel={1}>
              <LeftNavigationTileLink
                label={item.label}
                numberOfItems={null}
                showActivityHighlight={null}
                showAlertHighlight={null}
                showWarnHighlight={null}
                url={routeConfig.url}
                icon={leftNavigationIconSet.collectionIcon}
                isLeftNavigationMinimised={false}
                isSecondary={true}
                isActive={() => isActive(item.counterPartyId)}
                onClick={() => {
                  dispatch(SelectionActions.setCounterpartyId(gridId, item.counterPartyId))
                }}
              />
              {collectionColors && <LeftNavigationCollectionStatuses subTotals={item.subTotals} colors={collectionColors} />}

              <LeftNavigationCountdownTimer minRfqEndTime={item.minRfqEndTime} minOtwEndTime={item.minOtwEndTime} />
            </LeftNavigationTile>
          ))}
        </SlidingPanel>
      )}
    </>
  )
}
